import {
  Typography,
  Box,
  useMediaQuery,
  DialogTitle,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useMemo } from 'react';

import { usePoliciesQuery } from '../../../api/getPolicies';
import PhoneIcon from '../../../assets/icons/Phone';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import theme from '../../../styles/theme';
import Dialog from '../../Dialog';
import Button from '../../styledComponents/Button';
import PhoneNumberLink from '../../styledComponents/PhoneNumberLink';
import QuoteFormRedirect from './QuoteFormRedirect';

interface ExpandedDialogProps {
  open: boolean;
  closeDialog: () => void;
  hasClubLink: boolean;
}

const TextItem = styled(Typography)`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.3;
  color: #373b49;
  text-align: center;
  max-width: 168px;
`;

const CrossSellExpandedCard: React.FC<ExpandedDialogProps> = ({
  open,
  closeDialog,
  hasClubLink,
}) => {
  const { data } = usePoliciesQuery();
  const crossSellInfo = data?.crossSellInfo;
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const { track } = useAnalytics();

  useEffect(() => {
    if (open) {
      track('Targeted Promotion Displayed', {
        event_type: 'Automated System Process',
        contents: 'Bundle and save up to:',
        location: 'Modal',
        displayed_phone_number: crossSellInfo?.clubInfo?.phoneNumber,
        displayed_auto_discount: crossSellInfo?.bundledDiscounts?.auto,
        displayed_property_discount: crossSellInfo?.bundledDiscounts?.home,
      });
    }
  }, [open]);

  const getPhoneNumber = () => {
    const number = crossSellInfo?.clubInfo?.phoneNumber?.split('.').join('');

    return `+1${number}`;
  };

  const hoursList = useMemo(() => {
    if (crossSellInfo?.clubInfo?.hoursOfOperation) {
      if (crossSellInfo?.clubInfo?.hoursOfOperation === '24/7') {
        return [['Mon - Sun', '24/7']];
      }
      const hours = crossSellInfo?.clubInfo?.hoursOfOperation
        .replace(/am/gi, 'a.m.')
        .replace(/pm/gi, 'p.m.');
      const splitList = hours.split(',');
      const list = [];
      for (let i = 0; i < splitList.length; i += 2) {
        list.push([splitList[i], splitList[i + 1]]);
      }
      return list;
    }
    return [];
  }, [crossSellInfo?.clubInfo?.hoursOfOperation]);

  return (
    <Dialog
      ariaLabelledby="CrossSellExpandedDialogLabel"
      data-testid="CrossSellExpandedDialog"
      open={open}
      closeDialog={closeDialog}
      mobileFixToBottom
      title={
        <DialogTitle
          id="CrossSellExpandedDialogLabel"
          sx={{
            fontSize: { mobile: 18, tablet: 28 },
            fontWeight: 700,
            textAlign: 'center',
            p: 0,
          }}
        >
          Save: Auto + Home
        </DialogTitle>
      }
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontFamily: 'Cabin',
          fontKerning: 'normal',
          fontSize: { mobile: 16, tablet: 18, desktop: 18 },
          lineHeight: '24px',
          color: colors.csaaDark,
          letterSpacing: { mobile: 0.3, tablet: 0.4, desktop: 0.4 },
          mt: 2,
        }}
      >
        Bundle and save up to:
      </Typography>
      <Box
        sx={{
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: colors.csaaNeutral,
          borderRadius: { mobile: 2, tablet: 5.5, desktop: 5.5 },
          padding: 2,
          display: 'flex',
          flexDirection: { mobile: 'column', tablet: 'row', desktop: 'row' },
          justifyContent: 'space-between',
          gap: { mobile: 2, tablet: 3, desktop: 3 },
          alignItems: 'center',
          mt: { mobile: 2, tablet: 3, desktop: 3 },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: { mobile: 0.2, tablet: 0.3, desktop: 0.3 },
            color: colors.csaaDark,
            textAlign: 'center',
            maxWidth: { mobile: 'none', tablet: '168px', desktop: '168px' },
          }}
        >
          {crossSellInfo?.bundledDiscounts?.auto}% on your auto insurance*
        </Typography>
        <Box
          sx={{
            width: { mobile: '100%', tablet: 2, desktop: 2 },
            height: { mobile: 2, tablet: 24, desktop: 24 },
            borderStyle: 'solid',
            borderColor: colors.csaaNeutral,
            borderWidth: 1,
          }}
        />
        <TextItem
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: { mobile: 0.2, tablet: 0.3, desktop: 0.3 },
            color: colors.csaaDark,
            textAlign: 'center',
            maxWidth: { mobile: 'none', tablet: '168px', desktop: '168px' },
          }}
        >
          {crossSellInfo?.bundledDiscounts?.home}% with AAA Home insurance*
        </TextItem>
      </Box>
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          {hoursList.map((hourItem) => (
            <Box
              sx={{ display: 'flex', gap: 1 }}
              key={hourItem[0] + hourItem[1]}
            >
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: 14,
                  lineHeight: '24px',
                  letterSpacing: 0.2,
                  color: colors.csaaBlack,
                  fontWeight: 500,
                }}
              >
                {hourItem[0]}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: 14,
                  lineHeight: '24px',
                  letterSpacing: 0.2,
                  color: colors.csaaBlack,
                  fontWeight: 400,
                }}
              >
                {hourItem[1]}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            gap: 1,
            py: 2,
            px: 1,
            mt: 3,
          }}
        >
          {hoursList.map((hourItem, index) => (
            <React.Fragment key={index}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    letterSpacing: 0.3,
                    color: colors.csaaBlack,
                    fontWeight: 500,
                  }}
                >
                  {hourItem[0]}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    letterSpacing: 0.3,
                    color: colors.csaaBlack,
                    fontWeight: 400,
                  }}
                >
                  {hourItem[1]}
                </Typography>
              </Box>
              {index !== hoursList.length - 1 && (
                <Box
                  sx={{
                    width: 2,
                    borderStyle: 'solid',
                    borderColor: colors.csaaNeutral,
                    borderWidth: 1,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mt: { mobile: 2, tablet: 0.5, desktop: 0.5 },
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            fontSize: { mobile: 16, tablet: 18 },
            lineHeight: '24px',
            letterSpacing: { mobile: 0.3, tablet: 0.4, desktop: 0.4 },
            color: colors.csaaMid,
          }}
        >
          Call us at
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          <PhoneIcon />
          <PhoneNumberLink
            phoneNumber={getPhoneNumber()}
            formattedPhoneNumber={crossSellInfo?.clubInfo?.phoneNumber}
            onClick={() =>
              track('Contact Accessed', {
                dialed_number: crossSellInfo?.clubInfo?.phoneNumber,
                event_type: 'Link Accessed',
                contents: 'Call us at',
                link: crossSellInfo?.clubInfo?.phoneNumber,
                location: 'Modal',
                method: 'Call',
              })
            }
          />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: { mobile: 10, tablet: 12, desktop: 12 },
          fontFamily: 'Cabin',
          fontKerning: 'normal',
          lineHeight: '16px',
          color: colors.csaaMid,
          letterSpacing: 0.2,
          mt: 1,
          mb: { mobile: 3, tablet: 5, desktop: 5 },
        }}
      >
        *Multi-policy and other discounts vary based on eligibility. Discounts
        not cumulative, certain restrictions apply.
      </Typography>
      {hasClubLink && (
        <QuoteFormRedirect>
          <Button
            sx={{ p: 1, width: 1 }}
            type="submit"
            onClick={() => {
              track('Promotion Clicked', {
                event_type: 'Link Accessed',
                contents: 'Bundle and save up to:',
                link: 'Get a quote',
                location: 'Modal',
              });
            }}
          >
            Get a quote
          </Button>
        </QuoteFormRedirect>
      )}
    </Dialog>
  );
};

export default CrossSellExpandedCard;
